import { CheckCircle2, Loader2 } from "lucide-react";

/**
 * <CompletenessGauge/> — single donut showing the client a composite
 * "matter completeness" score across steps, deep data, legal requirements
 * and confirmed decisions.
 *
 * Reads the `completeness` block from the client portal response:
 *   { pct, all_done, client_done/total, legal_done/total, decision_done/total }
 */
export default function CompletenessGauge({ completeness }) {
  if (!completeness) return null;
  const pct = Math.max(0, Math.min(100, completeness.pct));
  const allDone = completeness.all_done;
  // SVG donut params.
  const size = 100, stroke = 10, r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const offset = C - (pct / 100) * C;
  const tone = allDone
    ? "stroke-emerald-500"
    : pct >= 66
      ? "stroke-[#0055FF]"
      : pct >= 33
        ? "stroke-amber-500"
        : "stroke-zinc-400";

  const headline = allDone
    ? "Your part is complete — your matter is now progressing with EH Inc"
    : pct >= 80
      ? `You're ${pct}% complete — almost there.`
      : pct >= 40
        ? `You're ${pct}% complete — keep going.`
        : `You're ${pct}% complete — let's get started.`;

  return (
    <div
      className="bg-white border border-zinc-200 flex items-center gap-6 p-6 flex-wrap"
      data-testid="completeness-gauge"
    >
      <div className="shrink-0 relative" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={r}
                  fill="none" stroke="#e4e4e7" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none"
            className={`${tone} transition-all duration-700`}
            strokeWidth={stroke}
            strokeDasharray={C}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-serif text-2xl font-medium" data-testid="completeness-pct">
            {pct}%
          </span>
        </div>
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-overline text-zinc-500 flex items-center gap-2">
          {allDone ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <Loader2 className="h-3 w-3 text-[#0055FF]" />}
          Your matter completeness
        </div>
        <h3 className="font-serif text-2xl tracking-tight mt-1" data-testid="completeness-headline">
          {headline}
        </h3>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <BreakdownCell label="Steps"    done={completeness.client_done}   total={completeness.client_total} />
          <BreakdownCell label="Legal"    done={completeness.legal_done}    total={completeness.legal_total} />
          <BreakdownCell label="Decisions" done={completeness.decision_done} total={completeness.decision_total} />
        </div>
      </div>
    </div>
  );
}

function BreakdownCell({ label, done, total }) {
  const ok = done === total;
  return (
    <div className={`px-2 py-1.5 border ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-zinc-200 bg-zinc-50 text-zinc-700"}`}>
      <div className="text-[10px] tracking-widest uppercase opacity-70">{label}</div>
      <div className="font-mono">{done} / {total}</div>
    </div>
  );
}
